/**
 * LEAGUE SETTINGS — the switches, in plain words. D-EZ-103 (D-EZ-83…87, D-EZ-98).
 *
 * Three pieces, used in three places:
 *
 *   <RealismRules>     the five realism house rules (four switches and the era
 *                      dial). League creation, "edit for next season", and an
 *                      exhibition's Advanced section.
 *   <ScoringRules>     the two season-sheet switches. Leagues only — an
 *                      exhibition scores no championship.
 *   <SettingsReadout>  the same, read-only, for the League settings panel.
 *
 * Every rule gets one line saying what it does. None of them shows a decision
 * number unless "Show rulebook citations" is on.
 */

import type { HouseRuleOptions } from '../domain/houseRules.ts';
import { HOUSE_RULES } from '../domain/houseRules.ts';
import type { SeasonHouseRules } from '../domain/seasonPlay.ts';
import {
  raceRuleLines,
  sheetRuleLines,
  STAGE_WORDS,
  SWITCH_RULES,
  withEra,
  withRule,
  type LeagueSettings,
  type StageMode,
} from '../lib/leagueSettings.ts';
import { Cite } from './Cite.tsx';

const decisionOf = (key: string) => HOUSE_RULES.find((r) => r.key === key)?.decision ?? null;

export function RealismRules({
  value,
  onChange,
  idPrefix,
}: {
  value: HouseRuleOptions;
  onChange: (next: HouseRuleOptions) => void;
  idPrefix: string;
}) {
  const lines = raceRuleLines(value);
  return (
    <fieldset className="rules">
      <legend className="rules__legend">Realism</legend>
      <p className="rules__intro">
        House rules that make a race look more like a modern Cup race. All on by default; turn any off for the
        printed game.
      </p>
      {lines
        .filter((l) => l.key !== 'era')
        .map((l) => (
          <label key={l.key} className="rules__row" htmlFor={`${idPrefix}-${l.key}`}>
            <input
              id={`${idPrefix}-${l.key}`}
              type="checkbox"
              checked={l.on}
              onChange={(e) =>
                onChange(withRule(value, l.key as (typeof SWITCH_RULES)[number], e.target.checked))
              }
            />
            <span className="rules__text">
              <b>{l.label}</b>
              <span>
                {l.summary}
                <Cite> ({decisionOf(l.key)})</Cite>
              </span>
            </span>
          </label>
        ))}
      <div className="rules__row rules__row--select">
        <span className="rules__text">
          <label htmlFor={`${idPrefix}-era`}>
            <b>Era</b>
          </label>
          <span>
            {lines.find((l) => l.key === 'era')?.summary}
            <Cite> ({decisionOf('era')})</Cite>
          </span>
        </span>
        <select
          id={`${idPrefix}-era`}
          value={value.era === '1990s' ? '1990s' : 'modern'}
          onChange={(e) => onChange(withEra(value, e.target.value === '1990s' ? '1990s' : 'modern'))}
        >
          <option value="modern">Modern</option>
          <option value="1990s">1990s (the printed game)</option>
        </select>
      </div>
    </fieldset>
  );
}

export function ScoringRules({
  value,
  onChange,
  idPrefix,
}: {
  value: SeasonHouseRules;
  onChange: (next: SeasonHouseRules) => void;
  idPrefix: string;
}) {
  const stage: StageMode = value.stagePoints ?? 'off';
  return (
    <fieldset className="rules">
      <legend className="rules__legend">Scoring</legend>
      <p className="rules__intro">Two switches from the original season sheet. Both off by default.</p>
      <label className="rules__row" htmlFor={`${idPrefix}-dnf`}>
        <input
          id={`${idPrefix}-dnf`}
          type="checkbox"
          checked={value.dnfScoresZero === true}
          onChange={(e) => {
            const next = { ...value };
            if (e.target.checked) next.dnfScoresZero = true;
            else delete next.dnfScoresZero;
            onChange(next);
          }}
        />
        <span className="rules__text">
          <b>No points for a DNF</b>
          <span>A car that is not running at the finish scores no finishing points.</span>
        </span>
      </label>
      <div className="rules__row rules__row--select">
        <span className="rules__text">
          <label htmlFor={`${idPrefix}-stages`}>
            <b>Stage points</b>
          </label>
          <span>{STAGE_WORDS[stage].summary}</span>
        </span>
        <select
          id={`${idPrefix}-stages`}
          value={stage}
          onChange={(e) => {
            const next = { ...value };
            const mode = e.target.value as StageMode;
            if (mode === 'off') delete next.stagePoints;
            else next.stagePoints = mode;
            onChange(next);
          }}
        >
          {(Object.keys(STAGE_WORDS) as StageMode[]).map((m) => (
            <option key={m} value={m}>
              {STAGE_WORDS[m].label}
            </option>
          ))}
        </select>
      </div>
    </fieldset>
  );
}

/**
 * Read-only. One row per rule: name, on/off, what it means. With `onlyChangedFrom`,
 * just the rules that differ from those settings.
 */
export function SettingsReadout({ settings, onlyChangedFrom }: { settings: LeagueSettings; onlyChangedFrom?: LeagueSettings }) {
  const all = [...raceRuleLines(settings.race), ...sheetRuleLines(settings.sheet)];
  const base = onlyChangedFrom
    ? new Map([...raceRuleLines(onlyChangedFrom.race), ...sheetRuleLines(onlyChangedFrom.sheet)].map((r) => [r.key, r.state]))
    : null;
  const rows = base ? all.filter((r) => base.get(r.key) !== r.state) : all;
  return (
    <dl className="rulesread">
      {rows.map((r) => (
        <div key={r.key} className="rulesread__row">
          <dt>{r.label}</dt>
          <dd>
            <b className={r.on ? 'rulesread__on' : 'rulesread__off'}>{r.state}</b> {r.summary}
          </dd>
        </div>
      ))}
    </dl>
  );
}
